import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditMateriality } from '../../../models/auditMateriality';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-materiality-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-materiality-list.component.html',
  styleUrl: './audit-materiality-list.component.scss'
})
export class AuditMaterialityListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditMaterialities = signal<AuditMateriality[]>([]);
  rightItem: AuditMateriality | undefined = undefined;
  matchedItems: AuditMateriality[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditMaterialities();
  }

  loadAuditMaterialities(): void {
    this.spinnerService.show();
    this.lutService.getAuditMaterialities().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortAuditMaterialities(data);
        this.auditMaterialities.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit materialities:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditMaterialities(auditMaterialities: AuditMateriality[]): AuditMateriality[] {
    return [...auditMaterialities].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditMateriality(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditMateriality: AuditMateriality): void {
  }

  onEditAuditMateriality(auditMateriality: AuditMateriality): void {
  }

  onDeleteAuditMateriality(auditMateriality: AuditMateriality): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditMateriality.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalAuditMaterialities = this.auditMaterialities();

          // 1. Optimistically remove from local state immediately
          this.auditMaterialities.update(auditMaterialities => auditMaterialities.filter(am => am.id !== auditMateriality.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAuditMateriality(auditMateriality.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit materiality deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting audit materiality:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.auditMaterialities.set(originalAuditMaterialities);
              this.toast.error(`<strong>Failed to delete Audit Materiality:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditMateriality, newName: string} | {item: AuditMateriality, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: AuditMateriality;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.auditMaterialities.update(auditMaterialities => {
      const updated = auditMaterialities.map(am => am.id === event.item.id ? updatedItem : am);
      return this.sortAuditMaterialities(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAuditMateriality(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit materiality updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.auditMaterialities.update(auditMaterialities => {
          const updated = auditMaterialities.map(am => am.id === event.item.id ? response : am);
          return this.sortAuditMaterialities(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit materiality:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.auditMaterialities.update(auditMaterialities =>
          auditMaterialities.map(am => am.id === event.item.id ? originalItem : am)
        );
        this.toast.error(`<strong>Failed to update Audit Materiality:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newAuditMateriality: AuditMateriality = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.auditMaterialities.update(auditMaterialities => this.sortAuditMaterialities([...auditMaterialities, newAuditMateriality]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAuditMateriality({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit materiality created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.auditMaterialities.update(auditMaterialities => {
          const updated = auditMaterialities.map(am => am.id === tempId ? response : am);
          return this.sortAuditMaterialities(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit materiality:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.auditMaterialities.update(auditMaterialities =>
          auditMaterialities.filter(am => am.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Materiality:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditMateriality, matchedItems?: AuditMateriality[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditMaterialityCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalAuditMaterialities = this.auditMaterialities();

        // 1. Optimistically remove merged items from local state
        this.auditMaterialities.update(auditMaterialities =>
          auditMaterialities.filter(am => !bulkMergeRequest.leftIds.includes(am.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeAuditMaterialitiesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit materialities merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadAuditMaterialities();
          },
          error: (error) => {
            console.error('Error merging audit materialities:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.auditMaterialities.set(originalAuditMaterialities);
            // Restore selection state for retry
            this.rightItem = originalAuditMaterialities.find(am => am.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditMaterialities.filter(am => bulkMergeRequest.leftIds.includes(am.id));
            this.toast.error(`<strong>Failed to merge Audit Materialities:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
